
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Calculator, GalleryVertical, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import heroWebp1600 from "@/assets/hero-bg-1600.webp";
import heroWebp800 from "@/assets/hero-bg-800.webp";

export const Hero = () => {
  const scrollToGallery = (e: React.MouseEvent) => {
    e.preventDefault();
    const gallerySection = document.getElementById('gallery');
    if (gallerySection) {
      gallerySection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      className="relative min-h-screen flex items-center"
      aria-label="Hero section showcasing secondary glazing solutions"
    >
      {/* LCP hero image — <picture> with WebP srcset + PNG fallback, no CLS */}
      <picture>
        <source
          type="image/webp"
          srcSet={`${heroWebp800} 800w, ${heroWebp1600} 1600w`}
          sizes="100vw"
        />
        <img
          src="/lovable-uploads/15bfa273-34e2-4ed4-b365-91776f63bb03.png"
          alt="Premium secondary glazing installation on a London period property"
          title="View our premium secondary glazing installation"
          {...{ fetchpriority: "high" }}
          loading="eager"
          decoding="sync"
          width={1600}
          height={1200}
          sizes="100vw"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ zIndex: 0 }}
        />
      </picture>
      <div className="absolute inset-0 bg-black opacity-60" aria-hidden="true" style={{ zIndex: 1 }}></div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32" style={{ zIndex: 2 }}>
        <div className="text-center">
          <h1 className="text-4xl tracking-tight font-bold text-white sm:text-5xl md:text-6xl font-montserrat animate-fade-in">
            <span className="block">Secondary Glazing London</span>{" "}
            <span className="block text-accent">For Period &amp; Heritage Homes</span>
          </h1>
          <p className="mt-3 max-w-md mx-auto text-base text-gray-300 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl font-inter">
            Bespoke acoustic and thermal secondary glazing, surveyed and fitted across London to make original windows quieter, warmer and more efficient.
          </p>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link to="/get-quote">
              <Button className="bg-accent hover:bg-accent-light text-primary font-semibold px-8 py-3 text-lg" aria-label="Request a free secondary glazing quote for your London property">
                Request a Free Quote
              </Button>
            </Link>
            <a href="#quote-calculator" onClick={(e) => { e.preventDefault(); document.getElementById('quote-calculator')?.scrollIntoView({ behavior: 'smooth' }); }}>
              <Button className="bg-white/90 hover:bg-white text-primary font-semibold px-8 py-3 text-lg flex items-center gap-2" aria-label="Calculate your secondary glazing cost estimate">
                <Calculator className="h-5 w-5" />
                Calculate Your Savings
              </Button>
            </a>
            <Link to="/secondary-glazing-benefits">
              <Button variant="outline" className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-3 text-lg">
                Explore Secondary Glazing Benefits
              </Button>
            </Link>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="bg-white/10 text-white border border-white/30 hover:bg-white/20 px-8 py-3 text-lg flex items-center gap-2">
                  Window Types
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 bg-white border border-gray-200 shadow-lg">
                <DropdownMenuItem asChild>
                  <Link to="/fixed-units-glazing" className="w-full px-4 py-2 hover:bg-gray-100 cursor-pointer">
                    Fixed Units
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/sliding-units-glazing" className="w-full px-4 py-2 hover:bg-gray-100 cursor-pointer">
                    Sliding Units
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/hinged-units-glazing" className="w-full px-4 py-2 hover:bg-gray-100 cursor-pointer">
                    Hinged Units
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/lift-out-units-glazing" className="w-full px-4 py-2 hover:bg-gray-100 cursor-pointer">
                    Lift Out Units
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/vertical-sliding-units-glazing" className="w-full px-4 py-2 hover:bg-gray-100 cursor-pointer">
                    Vertical Sliding Units
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/horizontal-sliding-units-glazing" className="w-full px-4 py-2 hover:bg-gray-100 cursor-pointer">
                    Horizontal Sliding Units
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <a href="#gallery" onClick={scrollToGallery}>
              <Button variant="ghost" className="bg-white/10 text-white border border-white/30 hover:bg-white/20 px-8 py-3 text-lg flex items-center gap-2">
                <GalleryVertical className="h-5 w-5" />
                View Gallery
              </Button>
            </a>
            <Link to="/secondary-glazing-images">
              <Button variant="ghost" className="bg-accent/10 text-white border border-accent/40 hover:bg-accent hover:text-accent-foreground px-8 py-3 text-lg">
                Full Gallery
              </Button>
            </Link>
            <Link to="/noise-reduction-secondary-glazing">
              <Button variant="contact" className="px-8 py-3 text-lg flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Noise Reduction Calculator
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
